import { angleTo } from '../../core/math';
import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

/** Stone-and-root crypt kit shared by the Hollowroot Crypt. */
const KIT: DungeonKit = {
  wall: { model: 'dungeon_wall', length: 8, depth: 1.2, scale: 0.75 },
  post: { model: 'pillar_intact', every: 3, scale: [0.8, 0.95] },
  light: { model: 'brazier', every: 3 },
  low: [
    { model: 'pillar_ruin', scale: [0.5, 0.75], weight: 2, tint: 0xc0b8ac },
    { model: 'rock_b', scale: [0.7, 1.1], tint: 0xa8a098 },
    { model: 'bones', weight: 1.5 },
  ],
  rocks: [
    { model: 'cliff_chunk', r: 2.9, scale: [0.45, 0.8], weight: 3 },
    { model: 'rock_a', r: 1.1, scale: [1.1, 1.9], tint: 0xb0a898 },
    { model: 'rock_c', r: 0.7, scale: [1.0, 1.8], tint: 0xb0a898 },
  ],
  clutter: [{ model: 'bones', weight: 3 }, { model: 'mushroom_cluster', weight: 2, scale: [0.5, 0.9] }, { model: 'rock_b', scale: [0.3, 0.5], tint: 0xa8a098 }],
  clutterChance: 0.3,
};

/**
 * HOLLOWROOT CRYPT (Act I story dungeon, levels 7–9). A collapsed stair from
 * the Emberwild ruins descends through an ossuary, a pillared root gallery
 * and the grubling shamans' den to the Chieftain's round chamber (r 16).
 * Critical path: entry hall (south) → boss chamber (north), braziers lit
 * along it; the west burial alcove is off-path and dark.
 *
 * Spawns: `start`, `boss`. Exits: `crypt_exit` (near the start) and
 * `crypt_exit_boss` (boss chamber, hidden until `crypt_cleared`), both to
 * Emberwild `fromCrypt`.
 */
export function buildCrypt(): ZoneLayout {
  const rng = new Rng('crypt');
  const d = new DungeonBuilder(
    {
      id: 'crypt', name: 'Hollowroot Crypt', subtitle: 'The Chieftain’s warren', kind: 'dungeon', biome: 'abyss',
      levelRange: [7, 9], music: 'dungeon', width: 160, depth: 208, seed: 2201,
      ground: ['cobble', 'dirt', 'rock', 'moss'], groundScale: [4, 5, 7, 5], ambient: 'motes', mapTint: 0x2a2420,
      wallHeight: 7, nearWallHeight: 1.1, accentAmount: 0.7, kit: KIT,
    },
    rng,
  );

  // Rooms first (corridors ramp between the floors they join).
  d.hall(0, 78, 7, 9, { y: 0.6, path: true }); // entry stair hall
  d.room(-20, 40, 11, { y: -0.5, path: true }); // ossuary
  d.room(-47, 44, 6, { y: -0.7 }); // burial alcove (off-path)
  d.hall(8, 0, 17, 8, { y: -1.2, path: true }); // root gallery
  d.room(12, -42, 12, { y: -2.2, path: true }); // shamans' den
  d.room(-8, -76, 16, { y: -3, path: true }); // chieftain's chamber
  d.corridor([{ x: 0, z: 70 }, { x: 0, z: 58 }, { x: -12, z: 48 }], 5.5, { path: true });
  d.corridor([{ x: -30, z: 42 }, { x: -42, z: 44 }], 4);
  d.corridor([{ x: -17, z: 32 }, { x: -12, z: 18 }, { x: -2, z: 6 }], 5, { path: true });
  d.corridor([{ x: 21, z: -6 }, { x: 27, z: -20 }, { x: 16, z: -35 }], 5, { path: true });
  d.corridor([{ x: 7, z: -51 }, { x: -3, z: -63 }], 6, { path: true });
  d.runner([{ x: 0, z: 84 }, { x: 0, z: 70 }, { x: 0, z: 58 }, { x: -12, z: 48 }, { x: -20, z: 40 }, { x: -17, z: 32 }, { x: -12, z: 18 }, { x: -2, z: 6 }, { x: 8, z: 0 }, { x: 21, z: -6 }, { x: 27, z: -20 }, { x: 16, z: -35 }, { x: 12, z: -42 }, { x: 7, z: -51 }, { x: -3, z: -63 }, { x: -8, z: -70 }]);

  // Entry: the way back up to Emberwild, just behind the start.
  d.spawn('start', 0, 77, Math.PI);
  d.litPortal({ id: 'crypt_exit', to: 'emberwild', toSpawn: 'fromCrypt', x: 0, z: 85, radius: 2.2, label: 'Emberwild Vale', kind: 'exit' }, 0);
  d.prop('pillar_intact', -5, 73, { collider: { r: 0.7 }, tint: 0xc8c0b4 });
  d.prop('pillar_intact', 5, 73, { collider: { r: 0.7 }, tint: 0xc8c0b4 });
  d.prop('rock_b', -5.5, 84, { scale: 0.8, tint: 0xa8a098, collider: { r: 0.6 } });
  d.prop('rock_a', 5.8, 83, { scale: 0.7, tint: 0xa8a098, collider: { r: 0.7 } });
  d.arch(0, 68.5, Math.PI, 5.5, 0x8c8378);

  // Ossuary.
  for (const [i, p] of DungeonBuilder.ring(-20, 40, 8, 6, 1).entries()) {
    d.prop(i % 2 === 0 ? 'pillar_intact' : 'pillar_ruin', p.x, p.z, { collider: { r: 0.7 }, tint: 0xc8c0b4, rot: i });
  }
  d.prop('sarcophagus', -29, 36, { rot: 0.15, collider: { w: 1.2, d: 2.5 }, tint: 0xc0b8ac });
  d.prop('sarcophagus', -28, 46, { rot: -0.3, collider: { w: 1.2, d: 2.5 }, tint: 0xc0b8ac });
  d.pack({ id: 'crypt_ossuary', monsters: [{ def: 'grubling', count: 5 }, { def: 'grubling_shaman', count: 1 }], x: -20, z: 39, radius: 5.5, level: 7 });

  // Burial alcove (off-path, unlit): a guarded chest and a tablet.
  d.pack({ id: 'crypt_alcove', monsters: [{ def: 'crypt_skeleton', count: 2 }], x: -46, z: 44, radius: 2.5, level: 8 });
  d.interact({ id: 'crypt_chest', kind: 'chest', x: -50, z: 45, data: { loot: 'crypt_chest' } });
  d.interact({ id: 'crypt_lore_tablet', kind: 'lore', x: -46, z: 40, data: { lore: 'crypt_tablet' } });
  d.prop('sarcophagus', -50, 47.5, { rot: Math.PI / 2 + 0.2, collider: { w: 1.2, d: 2.5 }, tint: 0xa8a098 });

  // Corridor ambush.
  d.pack({ id: 'crypt_stair_guard', monsters: [{ def: 'crypt_skeleton', count: 2 }], x: -12, z: 19, radius: 2, level: 7 });

  // Root gallery: two pillar rows; roots break through the north wall.
  for (const x of [-3, 5, 13, 21]) {
    d.prop('pillar_intact', x, -4.6, { collider: { r: 0.7 }, tint: 0xc8c0b4 });
    d.prop(x === 13 ? 'pillar_ruin' : 'pillar_intact', x, 4.6, { collider: { r: 0.7 }, tint: 0xc8c0b4 });
  }
  for (const [x, s] of [[-6, 1.5], [4, 1.8], [16, 1.4]] as const) d.prop('tree_dead', x, -10.2, { scale: s, rot: x, tint: 0x8a7058 });
  d.prop('log', 9, 5.8, { rot: 0.3, collider: { w: 2.7, d: 0.7 }, tint: 0x8a7058 });
  d.pack({ id: 'crypt_gallery_bones', monsters: [{ def: 'crypt_skeleton', count: 4 }], x: 1, z: 0, radius: 4.5, level: 8 });
  d.pack({ id: 'crypt_gallery_grubs', monsters: [{ def: 'grubling', count: 4 }, { def: 'grubling_brute', count: 1 }], x: 18, z: 0, radius: 4, level: 8 });

  // Shamans' den: a grubling camp around a ritual fire.
  d.prop('campfire', 17, -46, { collider: { r: 0.7 } });
  d.prop('tent', 4, -46.5, { rot: 2.4, collider: { w: 3.4, d: 3.4 }, tint: 0xa89070 });
  d.prop('altar', 13, -52, { collider: { w: 2, d: 1.2 } });
  d.prop('banner_pole', 20, -39, { collider: { r: 0.35 }, tint: 0xb080d0 });
  d.prop('banner_pole', 5, -36, { collider: { r: 0.35 }, tint: 0xb080d0 });
  d.prop('bones', 15, -44);
  d.prop('bones', 9, -38);
  d.pack({ id: 'crypt_den', monsters: [{ def: 'grubling', count: 4 }, { def: 'grubling_shaman', count: 2 }, { def: 'grubling_brute', count: 1 }], x: 11, z: -41, radius: 6, level: 9 });

  // Chieftain's chamber (r 16): open floor, trophies at the rim.
  d.arch(-1.6, -61.3, angleTo({ x: 7, z: -51 }, { x: -3, z: -63 }), 6, 0x8c8378);
  d.paintRing(-8, -76, 6.2, 7.2, 1);
  d.paintCircle(-8, -76, 2.2, 1);
  d.spawn('boss', -8, -78, 0);
  d.pack({ id: 'crypt_boss', monsters: [{ def: 'grubling_chieftain', count: 1 }], x: -8, z: -78, radius: 1, level: 9, tags: ['boss'] });
  d.litPortal({ id: 'crypt_exit_boss', to: 'emberwild', toSpawn: 'fromCrypt', x: -8, z: -89, radius: 2.4, label: 'Return to Emberwild', kind: 'exit', requires: 'crypt_cleared' }, Math.PI);
  for (const p of [DungeonBuilder.clock(-8, -76, 17.6, 10), DungeonBuilder.clock(-8, -76, 17.6, 2)]) d.prop('tree_dead', p.x, p.z, { scale: 1.6, tint: 0x806650, rot: p.x });

  return d.build();
}
